// filepath: apps/web/src/features/cabinet/Sidebar.tsx
//
// FR-UI-CABINET-1 AC-1 / AC-2 / AC-5 / AC-6 / AC-7 — the persistent
// left-nav sidebar.
//
// The sidebar renders EXACTLY the items `useNavManifest()` entitles.
// It never client-guesses nav (INV-2 — nav visibility is cosmetic UX
// derived from a server-issued signal; every linked surface remains
// server-gated by RBAC middleware regardless).
//
// Sections:
//   - ALWAYS: "My Cabinet" (/) + "My Tasks" (/tasks).
//   - Processes (/processes/instances/failed) — iff processesAccess.
//   - Entities — iff readableEntityTypes is non-empty. v1: NEVER
//     rendered (AC-5 deferred). When `/api/me` lands, this section
//     renders one link per readable type → /lists/:entityType.
//   - Admin (/admin tile grid) — iff adminAccess. Fail-closed.
//
// Nav items are real `<a href>` elements (progressive enhancement /
// keyboard-focusable / Enter-to-activate — FR-UI-CORE-9) whose
// `onClick` calls `navigate(path)` (pushState + popstate) and
// prevents the default full-page navigation — mirroring
// `DrilldownLink.tsx` exactly. The `href` is the real URL so the
// link is usable without JS and copy-pasteable.
//
// INV-9: every label renders as a React text node via `t(key,
// fallback)`. Zero `dangerouslySetInnerHTML`. The static-guard sweep
// over `apps/web/src/` (FR-UI-CORE-5) covers this subtree.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { useNavManifest } from "./useNavManifest";
import { usePathname, navigate } from "./usePathname";
import { useProcessCount } from "./chrome/useProcessCount";
import { useTaskCount } from "./chrome/useTaskCount";
import {
  CABINET_LANDING_PATH,
  TASKS_PATH,
  PROCESSES_PATH,
  ADMIN_INDEX_PATH,
} from "./types";
import {
  directoryNavItems,
  type DirectoryNavItem,
} from "@/features/directory/directoryNav";
import {
  NEUTRAL,
  SEMANTIC,
  SPACE,
  RADIUS_FULL,
  RADIUS_MD,
  SIDEBAR_WIDTH,
} from "./styles";

interface NavItemDef {
  readonly id: string;
  readonly href: string;
  readonly labelKey: string;
  readonly fallback: string;
  readonly testId: string;
  /** Optional numeric badge count (FR-UI-CABINET-2 AC-14, AC-15).
   *  When undefined or 0, no badge is rendered. */
  readonly badgeCount?: number;
}

/** A single nav link. Active when the current pathname starts with
 *  the item's path prefix (so /admin/users highlights the Admin
 *  item). The cabinet landing ("/") is matched exactly so it does
 *  not highlight on every path. */
function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

function NavItem({ item }: { item: NavItemDef }): React.ReactElement {
  const { t } = useTranslation();
  const pathname = usePathname();
  const active = isActive(pathname, item.href);

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    // Mirrors DrilldownLink: prefer SPA nav over default full-page
    // navigation. Middle-click / modified-click (cmd/ctrl) falls
    // through to the browser's default so users can open in a new tab.
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.altKey ||
      e.ctrlKey ||
      e.shiftKey
    ) {
      return;
    }
    e.preventDefault();
    navigate(item.href);
  };

  return (
    <li style={{ listStyle: "none" }}>
      <a
        href={item.href}
        data-testid={item.testId}
        data-active={active ? "true" : "false"}
        aria-current={active ? "page" : undefined}
        onClick={onClick}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: `${SPACE[2]} ${SPACE[3]}`,
          borderRadius: RADIUS_MD,
          fontSize: "14px",
          textDecoration: "none",
          color: active ? NEUTRAL.sidebarActiveText : NEUTRAL.sidebarText,
          background: active ? SEMANTIC.primaryPressed : "transparent",
          cursor: "pointer",
          gap: SPACE[2],
        }}
      >
        <span>{t(item.labelKey, item.fallback)}</span>
        {item.badgeCount !== undefined && item.badgeCount > 0 ? (
          <span
            data-testid={`${item.testId}-badge`}
            aria-label={`${item.badgeCount} pending`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "20px",
              height: "20px",
              padding: "0 6px",
              borderRadius: RADIUS_FULL,
              background: SEMANTIC.danger,
              color: "var(--text-on-primary)",
              fontSize: "11px",
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {item.badgeCount > 99 ? "99+" : item.badgeCount}
          </span>
        ) : null}
      </a>
    </li>
  );
}

function NavSectionLabel({
  labelKey,
  fallback,
}: {
  labelKey: string;
  fallback: string;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <li
      // ISS-434 (ui-conformance, sweep #320 finding 5): the BA
      // captured the sidebar as a "flat list — no section dividers"
      // because the original implementation only relied on
      // `marginTop` + an uppercase label. The new design adds a
      // visible border-top so the section grouping is unambiguous
      // to a casual reviewer (matches the ui_kit baseline).
      data-testid={`cabinet-nav-section-label-${labelKey.split(".").pop()}`}
      style={{
        listStyle: "none",
        marginTop: SPACE[4],
        marginBottom: SPACE[2],
        padding: `${SPACE[2]} ${SPACE[3]} 0 ${SPACE[3]}`,
        borderTop: `1px solid ${NEUTRAL.borderDefault}`,
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: NEUTRAL.sidebarSectionLabel,
      }}
    >
      {t(labelKey, fallback)}
    </li>
  );
}

/**
 * Sidebar — the persistent left nav. Renders a dark rail containing
 * the always-present items, the Processes link, the (v1-empty)
 * Entities section, and the Admin link. Composed by `<TenantShell>`.
 */
export function Sidebar(): React.ReactElement {
  const { t } = useTranslation();
  const manifest = useNavManifest();
  // FR-UI-CABINET-2 AC-14 / AC-15: badge counts on Processes + Tasks.
  // The hooks mount a TanStack Query for each (shared cache with the
  // existing dashboard / inbox surfaces — no duplicate fetches).
  const processCount = useProcessCount();
  const taskCount = useTaskCount();

  const alwaysItems: readonly NavItemDef[] = [
    {
      id: "cabinet",
      href: CABINET_LANDING_PATH,
      labelKey: "cabinet.nav.cabinet",
      fallback: "My Cabinet",
      testId: "cabinet-nav-cabinet",
    },
    {
      id: "tasks",
      href: TASKS_PATH,
      labelKey: "cabinet.nav.tasks",
      fallback: "My Tasks",
      testId: "cabinet-nav-tasks",
      badgeCount: taskCount,
    },
  ];

  // Built mutably (pushed conditionally below), then frozen-consumed
  // by .map(). Kept as a local mutable array so the AC-5 descope can
  // append one entry per readable entity type without reallocating.
  const recordsItems: NavItemDef[] = [];
  if (manifest.processesAccess) {
    recordsItems.push({
      id: "processes",
      href: PROCESSES_PATH,
      labelKey: "cabinet.nav.processes",
      fallback: "Processes",
      testId: "cabinet-nav-processes",
      badgeCount: processCount,
    });
  }
  // AC-5 descope: readableEntityTypes is [] in v1, so the Entities
  // sub-section renders no links. When /api/me lands, push one link
  // per readable type → /lists/:entityType here. The section label
  // is only rendered when at least one records item exists, so v1
  // shows only the Processes link under "Records".
  for (const entityType of manifest.readableEntityTypes) {
    recordsItems.push({
      id: `entity-${entityType}`,
      href: `/lists/${encodeURIComponent(entityType)}`,
      labelKey: "cabinet.nav.entity",
      fallback: entityType,
      testId: `cabinet-nav-entity-${entityType}`,
    });
  }

  return (
    <aside
      data-testid="cabinet-sidebar"
      style={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        background: NEUTRAL.surfaceSidebar,
        color: NEUTRAL.sidebarText,
        padding: `${SPACE[4]} 0`,
        overflowY: "auto",
      }}
    >
      <nav
        aria-label={t("cabinet.nav.label", "Workspace sections")}
        data-testid="cabinet-nav"
      >
        <ul style={{ margin: 0, padding: 0 }}>
          {alwaysItems.map((item) => (
            <NavItem key={item.id} item={item} />
          ))}

          {recordsItems.length > 0 && (
            <NavSectionLabel
              labelKey="cabinet.nav.section.records"
              fallback="Records"
            />
          )}
          {recordsItems.map((item) => (
            <NavItem key={item.id} item={item} />
          ))}

          {/* FR-UI-DIR-1: Directory section. Renders iff the
              directoryAccess manifest signal is true. The items
              come from features/directory/directoryNav.ts so the
              design composition stays the source of truth. */}
          {directoryNavItems(manifest).length > 0 && (
            <NavSectionLabel
              labelKey="cabinet.nav.section.directory"
              fallback="Directory"
            />
          )}
          {directoryNavItems(manifest).map((item: DirectoryNavItem) => (
            <NavItem
              key={item.id}
              item={{
                id: item.id,
                href: item.href,
                labelKey: item.labelKey,
                fallback: item.fallback,
                testId: item.testId,
              }}
            />
          ))}

          {manifest.adminAccess && (
            <NavSectionLabel
              labelKey="cabinet.nav.section.admin"
              fallback="Admin"
            />
          )}
          {manifest.adminAccess && (
            <NavItem
              item={{
                id: "admin",
                href: ADMIN_INDEX_PATH,
                labelKey: "cabinet.nav.admin",
                fallback: "Admin",
                testId: "cabinet-nav-admin",
              }}
            />
          )}
        </ul>
      </nav>
    </aside>
  );
}
